import { cn } from '@v2/shared/lib/utils';
import { SKELETON_SURFACE } from '@v2/shared/components/Skeleton';

import { COVER_HEIGHT, COVER_WIDTH, POSTCARD_HEIGHT } from './ProductCard';

type Props = {
  /**
   * Draw with the shimmer rather than the pulse.
   *
   * The two callers want different things from the same shape. The windowing draws
   * these by the hundred — every tile outside the render window is one — and they are
   * off screen by definition, so they wear the pulse: one animated element per tile,
   * animating opacity. The loading screen draws six, in front of somebody who is
   * waiting, so it can afford the surface that travels a gradient and repaints.
   */
  shimmer?: boolean;
  /**
   * Stand in for a postcard: its box, a card-shaped picture and its one button.
   *
   * A postcard's tile is POSTCARD_HEIGHT rather than the cover's, so the cover-height
   * placeholder would be 22-99px taller than the tile it swaps with.
   */
  postcard?: boolean;
};

/**
 * A tile's placeholder: while it is out of view, and while the sale is still loading.
 *
 * It must be the same size as the tile it stands in for, or scrolling past a skeleton
 * that then becomes a real product moves everything below it and the page walks away
 * under the customer's finger. That is why it imports the tile's own cover box rather
 * than restating it: a skeleton has no content to take its size from, and two copies of
 * a responsive size are two copies to keep in step at five breakpoints. The same
 * argument is why the loading screen draws this rather than a card shape of its own.
 *
 * `aria-hidden`, and the section carries the real count: a screen reader announcing
 * "loading" forty times is worse than announcing nothing, and while the page is windowing
 * nothing is loading — the data is already here, only the markup is not.
 */
export default function ProductSkeleton({ shimmer = false, postcard = false }: Props) {
  const bar = shimmer ? SKELETON_SURFACE : 'bg-slate-100';

  return (
    <div className="p-3" aria-hidden="true">
      <div className={cn('flex gap-4', postcard ? POSTCARD_HEIGHT : COVER_HEIGHT, !shimmer && 'animate-pulse')}>
        <div className={cn(COVER_WIDTH, 'shrink-0 rounded', bar, postcard && 'self-center aspect-[720/488]')} />
        <div className="flex-grow flex flex-col gap-3 py-1">
          <div className={cn('h-5 w-3/4 rounded', bar)} />
          <div className={cn('h-6 w-1/3 rounded', bar)} />
          {!postcard && <div className={cn('h-4 w-1/2 rounded', bar)} />}
          <div className="mt-auto flex flex-col gap-2">
            {!postcard && <div className={cn('h-9 w-full rounded', bar)} />}
            <div className={cn('h-9 w-full rounded', bar)} />
          </div>
        </div>
      </div>
    </div>
  );
}
